import React from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import { ModalFooter } from '@amzn/meridian/modal';
import Select from '@amzn/meridian/select/select';
import SelectOption from '@amzn/meridian/select/select-option';
import { FormattedMessage } from 'react-intl';
import DataState from '../../../../Types/DataState';
import { TeamProfile } from '../../../../Types';

interface AssignProxyStepProps {
  proxyResponseMessage: string;
  selectedTMUserId: string;
  selectedProxyUsername: string;
  teamProfile: TeamProfile;
  addProxyState: (typeof DataState)[keyof typeof DataState];
  onAddProxy: () => void;
  onClickFooterButton: () => void;
  setAddProxyState: (state: (typeof DataState)[keyof typeof DataState]) => void;
  setSelectedTMUserId: (id: string) => void;
  setSelectedProxyUserName: (name: string) => void;
  getUsersBeingProxied: () => string[];
  cscEntityPrefix: string;
}

export const AssignProxyStep: React.FC<AssignProxyStepProps> = ({
  proxyResponseMessage,
  selectedTMUserId,
  selectedProxyUsername,
  teamProfile,
  addProxyState,
  onAddProxy,
  onClickFooterButton,
  setAddProxyState,
  setSelectedTMUserId,
  setSelectedProxyUserName,
  getUsersBeingProxied,
  cscEntityPrefix,
}) => (
  <Column spacing='400'>
    <Box>
      <Box spacingInset='none none 200 none'>
        <Text type='h100'>
          <FormattedMessage
            id='cs_perfportal_om_tm_input'
            defaultMessage='TM to be proxied'
          />
        </Text>
      </Box>
      <Select
        value={selectedTMUserId}
        onChange={setSelectedTMUserId}
        placeholder='Select'
        size='large'
      >
        {teamProfile?.reportingTeam
          .filter(member => {
            const memberFullId = `${cscEntityPrefix}:${member.relatedUser.userID}`;
            return (
              member.isManager && !getUsersBeingProxied().includes(memberFullId)
            );
          })
          .map(member => (
            <SelectOption
              key={member.relatedUser.userID}
              value={member.relatedUser.userID}
              label={`${member.relatedUser.displayName}`}
            />
          ))}
      </Select>
    </Box>

    <Box>
      <Box spacingInset='none none 200 none'>
        <Text type='h100'>
          <FormattedMessage
            id='cs_perfportal_om_proxy_input'
            defaultMessage='TM to assign as the proxy'
          />
        </Text>
      </Box>
      <Select
        value={selectedProxyUsername}
        onChange={setSelectedProxyUserName}
        placeholder='Select'
        size='large'
      >
        {teamProfile?.reportingTeam
          .filter(
            member =>
              member.relatedUser.userID !== selectedTMUserId && member.isManager
          )
          .map(member => (
            <SelectOption
              key={member.relatedUser.userID}
              value={member.relatedUser.userName}
              label={`${member.relatedUser.displayName}`}
            />
          ))}
      </Select>
    </Box>

    <Text type='b100'>
      <FormattedMessage
        id='cs_perfportal_proxy_notification'
        defaultMessage='The assigned proxy will be notified immediately.'
      />
    </Text>

    <ModalFooter>
      <Row alignmentHorizontal='end' widths='fit'>
        <Button type='secondary' size='small' onClick={onClickFooterButton}>
          <FormattedMessage id='cs_perfportal_cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          type='primary'
          size='small'
          onClick={onAddProxy}
          minWidth={100}
          disabled={
            !selectedTMUserId ||
            !selectedProxyUsername ||
            addProxyState === DataState.LOADING ||
            !teamProfile
          }
        >
          {addProxyState === DataState.LOADING ? (
            <Row alignmentVertical='center' spacing='200'>
              <Loader size='small' />
            </Row>
          ) : (
            <FormattedMessage
              id='cs_perfportal_assign_proxy'
              defaultMessage='Assign Proxy'
            />
          )}
        </Button>
      </Row>
    </ModalFooter>
  </Column>
);
